import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function DraftRunbookList() {
    const navigate = useNavigate();
    const [draftRunbooks, setDraftRunbooks] = useState([]);
    const [filteredRunbooks, setFilteredRunbooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTribe, setFilterTribe] = useState('');
    const [filterSolution, setFilterSolution] = useState('');
    const [tribes, setTribes] = useState([]);
    const [solutions, setSolutions] = useState([]);
    const [selectedRunbookId, setSelectedRunbookId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    useEffect(() => {
        fetchDraftRunbooks();
    }, []);

    useEffect(() => {
        filterRunbooks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftRunbooks, searchTerm, filterTribe, filterSolution]);

    const fetchDraftRunbooks = async () => {
        try {
            setLoading(true);
            const response = await apiCall('/runbooks/drafts', {
                method: 'GET'
            });

            setDraftRunbooks(response.drafts || []);
            
            // Extract unique tribes and solutions for filters
            const uniqueTribes = [...new Set(response.drafts.map(rb => rb.Tribe))].filter(Boolean).sort();
            const uniqueSolutions = [...new Set(response.drafts.map(rb => rb.Solution))].filter(Boolean).sort();
            setTribes(uniqueTribes);
            setSolutions(uniqueSolutions);
            
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch draft runbooks:', err);
            setError(err.message || 'Failed to load draft runbooks');
            setLoading(false);
        }
    };

    const filterRunbooks = () => {
        let filtered = [...draftRunbooks];

        // Search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(rb =>
                rb.Title?.toLowerCase().includes(term) ||
                rb.Runbook_ID?.toLowerCase().includes(term) ||
                rb.Tags?.some(tag => tag.toLowerCase().includes(term))
            );
        }

        // Tribe filter
        if (filterTribe) {
            filtered = filtered.filter(rb => rb.Tribe === filterTribe);
        }

        // Solution filter
        if (filterSolution) {
            filtered = filtered.filter(rb => rb.Solution === filterSolution);
        }

        setFilteredRunbooks(filtered);
    };

    const handleReviewClick = (runbookId) => {
        navigate(`/draft-runbooks/review/${runbookId}`);
    };

    const handleCheckboxChange = (runbookId) => {
        setSelectedRunbookId(prev => (prev === runbookId ? null : runbookId));
    };

    const handleDeleteDraft = async (runbookId, e) => {
        if (e) e.stopPropagation();
        
        const idToDelete = runbookId || selectedRunbookId;
        if (!idToDelete) return;
        
        if (!window.confirm(`Are you sure you want to delete draft runbook ${idToDelete}?`)) {
            return;
        }

        try {
            await apiCall(`/runbooks/drafts/${idToDelete}`, {
                method: 'DELETE'
            });
            
            // Remove from local state
            setDraftRunbooks(prev => prev.filter(rb => rb.Runbook_ID !== idToDelete));
            setSelectedRunbookId(null);
            alert('Draft runbook deleted successfully');
        } catch (err) {
            console.error('Failed to delete draft:', err);
            alert('Failed to delete draft runbook: ' + (err.message || 'Unknown error'));
        }
    };

    const totalPages = Math.ceil(filteredRunbooks.length / rowsPerPage);
    const currentDrafts = filteredRunbooks.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading draft runbooks...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error</h4>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={fetchDraftRunbooks}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Draft Runbooks</h2>
                <button 
                    className="btn btn-secondary"
                    onClick={() => navigate('/runbook_list')}
                >
                    Back to Runbooks
                </button>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by title, ID, or tags..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Tribe</label>
                            <select
                                className="form-select"
                                value={filterTribe}
                                onChange={(e) => setFilterTribe(e.target.value)}
                            >
                                <option value="">All Tribes</option>
                                {tribes.map(tribe => (
                                    <option key={tribe} value={tribe}>{tribe}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Solution</label>
                            <select
                                className="form-select"
                                value={filterSolution}
                                onChange={(e) => setFilterSolution(e.target.value)}
                            >
                                <option value="">All Solutions</option>
                                {solutions.map(solution => (
                                    <option key={solution} value={solution}>{solution}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {(searchTerm || filterTribe || filterSolution) && (
                        <button
                            className="btn btn-sm btn-outline-secondary mt-2"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterTribe('');
                                setFilterSolution('');
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <table className="table table-bordered table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th style={{width: '50px'}}></th>
                            <th>Runbook ID</th>
                            <th style={{ textAlign: 'left' }}>Runbook Title</th>
                            <th>Tribe</th>
                            <th style={{ textAlign: 'left' }}>Solution</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentDrafts.length > 0 ? (
                            currentDrafts.map((runbook) => (
                                <tr 
                                    key={runbook.Runbook_ID}
                                    className={selectedRunbookId === runbook.Runbook_ID ? 'table-active' : ''}
                                >
                                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedRunbookId === runbook.Runbook_ID}
                                            onChange={() => handleCheckboxChange(runbook.Runbook_ID)}
                                        />
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <a 
                                            href={`/draft-runbooks/review/${runbook.Runbook_ID}`} 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleReviewClick(runbook.Runbook_ID);
                                            }}
                                            className="text-decoration-none"
                                        >
                                            {runbook.Runbook_ID}
                                        </a>
                                    </td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{runbook.Title}</td>
                                    <td style={{ verticalAlign: 'middle' }}>{runbook.Tribe}</td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{runbook.Solution}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-4">
                                    <div className="text-muted">
                                        <p>No draft runbooks found.</p>
                                        {searchTerm || filterTribe || filterSolution ? (
                                            <small>Try adjusting your filters</small>
                                        ) : (
                                            <small>All imported runbooks have been reviewed</small>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <nav aria-label="Draft runbook pagination">
                    <div className="d-flex justify-content-center align-items-center mt-4">
                        <button 
                            className="btn btn-outline-secondary me-2" 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span className="mx-3">
                            Page {currentPage} of {totalPages} ({filteredRunbooks.length} draft{filteredRunbooks.length !== 1 ? 's' : ''})
                        </span>
                        <button 
                            className="btn btn-outline-secondary ms-2" 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </nav>
            )}

            {/* Action Buttons */}
            {selectedRunbookId && (
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button 
                        onClick={() => handleReviewClick(selectedRunbookId)} 
                        className="btn btn-success"
                        title={`Review runbook ${selectedRunbookId}`}
                    >
                        <i className="bi bi-eye me-1"></i>
                        Review & Approve Selected
                    </button>
                    <button 
                        onClick={(e) => handleDeleteDraft(null, e)} 
                        className="btn btn-danger"
                        title={`Delete runbook ${selectedRunbookId}`}
                    >
                        <i className="bi bi-trash me-1"></i>
                        Delete Selected
                    </button>
                </div>
            )}
        </div>
    );
}

export default DraftRunbookList;
